import type { RequestInterceptor } from '../../types';

/**
 * Keeps SSR traffic inside Kubernetes by rewriting requests to an in-cluster
 * Service. An empty namespace selects a same-namespace Service, while undefined
 * preserves the public URL. Relative harness URLs use a temporary parsing base
 * that is replaced with the internal destination below.
 */
export const internalNetworkInterceptor: RequestInterceptor = (ctx, next) => {
    if (typeof window !== 'undefined' || !ctx.options?.networkConfig) {
        return next(ctx);
    }

    const { networkConfig, ...restOptions } = ctx.options;
    const { internalNamespace, internalPort, internalService, internalHeaders } = networkConfig;

    // Empty namespace means a same-namespace Service; undefined keeps the public host.
    if (!internalPort || !internalService || internalNamespace === undefined) {
        return next(ctx);
    }

    const inputUrl =
        typeof ctx.input === 'string' ? ctx.input : ctx.input instanceof URL ? ctx.input.href : ctx.input.url;
    // Supply a temporary origin for relative SSR URLs; it is replaced below.
    const url = new URL(inputUrl, 'https://same-origin.invalid');

    url.protocol = 'http';
    url.hostname = internalNamespace ? `${internalService}.${internalNamespace}.svc.cluster.local` : internalService;
    url.port = internalPort.toString();

    return next({
        input: url,
        init: {
            ...ctx.init,
            headers: {
                ...ctx.init?.headers,
                ...internalHeaders
            }
        },
        options: restOptions
    });
};
